import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block relative overflow-hidden rounded-lg cursor-pointer"
      >
        <div className="aspect-[16/9] w-full overflow-hidden rounded-lg">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent rounded-lg" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs bg-accent-500 text-background-50 px-2 py-1 rounded-md font-medium whitespace-nowrap">
              精选推荐
            </span>
            <span className="text-xs text-background-50/80">{article.publishDate}</span>
            <span className="text-xs text-background-50/80">{article.readTime}</span>
          </div>
          <h3 className="text-lg md:text-xl font-heading font-bold text-background-50 group-hover:text-accent-300 transition-colors duration-200 line-clamp-2">
            {article.title}
          </h3>
          <p className="text-background-50/70 text-sm mt-1 line-clamp-2 hidden sm:block">
            {article.excerpt}
          </p>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
      >
        <div className="w-20 h-14 flex-shrink-0 overflow-hidden rounded-md">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
            <span>{article.publishDate}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[16/9] w-full overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-1 rounded-md font-medium whitespace-nowrap">
            {article.tags[0]}
          </span>
          <span className="text-xs text-foreground-500">{article.readTime}</span>
        </div>
        <h3 className="text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>{article.author}</span>
          <span>{article.publishDate}</span>
        </div>
      </div>
    </Link>
  );
}